import React, { useState } from 'react'
import { BankQuestion } from '../../services/bankQuestionService_moi'
import QuestionRenderer from '../quiz/QuestionRenderer'
import KaTeXRenderer from '../common/KaTeXRenderer'

const ImageDisplayTest: React.FC = () => {
  const [testMode, setTestMode] = useState<'question' | 'explanation' | 'both'>('both')

  // Test data với hình ảnh
  const testQuestion: BankQuestion = {
    id: 'test-image-question',
    type: 'trac_nghiem_nhieu_lua_chon',
    cau_hoi: 'Quan sát hình ảnh dưới đây và chọn đáp án đúng về định luật Newton thứ nhất:',
    lua_chon: [
      'Vật sẽ tiếp tục chuyển động thẳng đều',
      'Vật sẽ dừng lại ngay lập tức',
      'Vật sẽ tăng tốc',
      'Vật sẽ chuyển động tròn'
    ],
    dap_an_dung: 'A',
    loi_giai: 'Theo định luật Newton thứ nhất, vật sẽ tiếp tục trạng thái chuyển động thẳng đều khi không có lực tác dụng. Xem hình minh họa dưới đây:',
    goi_y: ['Nhớ định luật quán tính', 'Xem xét trạng thái ban đầu của vật'],
    muc_do: 'M2',
    diem: 2,
    hinh_anh: '/images/newton-law-1.jpg', // Test image path
    hinh_anh_giai_thich: '/images/newton-law-1-explanation.jpg' // Test explanation image
  }

  const testQuestionNoImages: BankQuestion = {
    ...testQuestion,
    id: 'test-no-image-question',
    cau_hoi: 'Câu hỏi không có hình ảnh: Định luật Newton thứ nhất phát biểu như thế nào?',
    hinh_anh: undefined,
    hinh_anh_giai_thich: undefined
  }

  const testQuestionOnlyQuestion: BankQuestion = {
    ...testQuestion,
    id: 'test-question-image-only',
    cau_hoi: 'Câu hỏi chỉ có hình ảnh câu hỏi:',
    hinh_anh_giai_thich: undefined
  }

  const testQuestionOnlyExplanation: BankQuestion = {
    ...testQuestion,
    id: 'test-explanation-image-only',
    cau_hoi: 'Câu hỏi chỉ có hình ảnh giải thích:',
    hinh_anh: undefined
  }

  const getCurrentQuestion = () => {
    switch (testMode) {
      case 'question':
        return testQuestionOnlyQuestion
      case 'explanation':
        return testQuestionOnlyExplanation
      case 'both':
      default:
        return testQuestion
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">
            🖼️ Test Hiển Thị Hình Ảnh Câu Hỏi
          </h1>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Chế độ test:
            </label>
            <select
              value={testMode}
              onChange={(e) => setTestMode(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="both">Cả hai hình ảnh</option>
              <option value="question">Chỉ hình ảnh câu hỏi</option>
              <option value="explanation">Chỉ hình ảnh giải thích</option>
            </select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Test trong QuizInterface style */}
            <div>
              <h2 className="text-xl font-semibold mb-4">📝 Giao diện làm bài (QuizInterface)</h2>
              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="text-lg font-bold text-gray-900 mb-4">
                  <KaTeXRenderer content={getCurrentQuestion().cau_hoi} />
                </div>

                {/* Question Image */}
                {getCurrentQuestion().hinh_anh && (
                  <div className="mb-6">
                    <div className="bg-white p-4 rounded-xl border border-gray-200">
                      <img
                        src={getCurrentQuestion().hinh_anh}
                        alt="Hình ảnh câu hỏi"
                        className="max-w-full h-auto rounded-lg shadow-sm mx-auto block"
                        onError={(e) => {
                          console.warn('Failed to load question image:', getCurrentQuestion().hinh_anh)
                          const target = e.currentTarget as HTMLImageElement
                          target.style.display = 'none'
                          // Show error message
                          const errorDiv = document.createElement('div')
                          errorDiv.className = 'text-center text-gray-500 py-4'
                          errorDiv.innerHTML = '⚠️ Không thể tải hình ảnh câu hỏi'
                          target.parentNode?.appendChild(errorDiv)
                        }}
                        onLoad={() => {
                          console.log('✅ Question image loaded successfully:', getCurrentQuestion().hinh_anh)
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Options */}
                <div className="space-y-3">
                  {getCurrentQuestion().lua_chon?.map((option, index) => (
                    <div key={index} className="p-3 border border-gray-200 rounded-lg bg-white">
                      <KaTeXRenderer content={option} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Test trong QuizSubmission style */}
            <div>
              <h2 className="text-xl font-semibold mb-4">📊 Kết quả bài làm (QuizSubmission)</h2>
              <div className="bg-gray-50 p-6 rounded-xl">
                <div className="font-bold text-gray-900 mb-4">
                  Câu 1: <KaTeXRenderer content={getCurrentQuestion().cau_hoi} />
                </div>

                {/* Question Image */}
                {getCurrentQuestion().hinh_anh && (
                  <div className="mb-6">
                    <img 
                      src={getCurrentQuestion().hinh_anh} 
                      alt="Hình ảnh câu hỏi" 
                      className="max-w-full h-auto rounded-xl shadow-md border border-gray-200"
                      onError={(e) => {
                        console.warn('Failed to load question image:', getCurrentQuestion().hinh_anh)
                        e.currentTarget.style.display = 'none'
                      }}
                    />
                  </div>
                )}

                {/* Explanation */}
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                  <span className="font-bold text-blue-800">💡 Giải thích: </span>
                  <div className="text-blue-700 inline">
                    <KaTeXRenderer content={getCurrentQuestion().loi_giai || ''} />
                  </div>
                  
                  {/* Explanation Image */}
                  {getCurrentQuestion().hinh_anh_giai_thich && (
                    <div className="mt-4">
                      <img 
                        src={getCurrentQuestion().hinh_anh_giai_thich} 
                        alt="Hình ảnh giải thích" 
                        className="max-w-full h-auto rounded-lg shadow-md border border-blue-300"
                        onError={(e) => {
                          console.warn('Failed to load explanation image:', getCurrentQuestion().hinh_anh_giai_thich)
                          const target = e.currentTarget as HTMLImageElement
                          target.style.display = 'none'
                          // Show error message
                          const errorDiv = document.createElement('div')
                          errorDiv.className = 'text-center text-gray-500 py-4'
                          errorDiv.innerHTML = '⚠️ Không thể tải hình ảnh giải thích'
                          target.parentNode?.appendChild(errorDiv)
                        }}
                        onLoad={() => {
                          console.log('✅ Explanation image loaded successfully:', getCurrentQuestion().hinh_anh_giai_thich)
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Test với QuestionRenderer component */}
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">🔧 QuestionRenderer Component</h2>
            <QuestionRenderer
              question={getCurrentQuestion()}
              questionNumber={1}
              selectedAnswer="A"
              showSolution={true}
            />
          </div>

          {/* Debug Info */}
          <div className="mt-8 bg-gray-100 p-4 rounded-xl">
            <h3 className="font-semibold mb-2">🔍 Debug Info:</h3>
            <pre className="text-sm text-gray-600 overflow-auto">
              {JSON.stringify({
                questionImage: getCurrentQuestion().hinh_anh,
                explanationImage: getCurrentQuestion().hinh_anh_giai_thich,
                hasQuestionImage: !!getCurrentQuestion().hinh_anh,
                hasExplanationImage: !!getCurrentQuestion().hinh_anh_giai_thich
              }, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImageDisplayTest